import * as React from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatDateTimeMediumDotted } from '@klareospace/pharos-lib-front-utils';
import { getFullName } from '@klareospace/pharos-lib-utils';
import type { ImportDetail } from '@klareospace/pharos-lib-types';
import { Card, CardContent } from '@klareospace/pharos-lib-web-utils';

import { computeSuccessRate } from '../utils';
import { useDownloadImportFile } from '../hooks';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ImportReportHeaderProps {
  importDoc: ImportDetail;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Report page header card — filename (clickable to download the original file), timestamp, importing user and success rate. */
export function ImportReportHeader({ importDoc }: ImportReportHeaderProps): React.JSX.Element {
  const { t, i18n } = useTranslation('imports');
  const { mutate: downloadImportFile } = useDownloadImportFile();

  const handleDownload = (): void => {
    downloadImportFile(importDoc);
  };

  const successRate = computeSuccessRate(importDoc);

  return (
    <Card className="rounded-xl">
      <CardContent className="flex items-center justify-between gap-4 p-5">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <button
              className="truncate text-sm font-semibold text-primary hover:underline"
              onClick={handleDownload}
              type="button"
            >
              {importDoc.fileName}
            </button>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {formatDateTimeMediumDotted(importDoc.createdAt, i18n.language)}
              {' · '}
              {t('report.importedBy', { name: getFullName(importDoc.user?.firstName, importDoc.user?.lastName) })}
            </p>
          </div>
        </div>
        <div className="shrink-0 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
          {t('report.successRate')} {successRate}%
        </div>
      </CardContent>
    </Card>
  );
}
